import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/Sidebar";
import { getMarketingPlanAttachment, getMarketingPlans } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { useToast } from "../../contexts/ToastContext";
import {
  getPermissionControllers,
  hasBrokerPermission,
  isBroker,
} from "../../utils/permissionHelper";
import PermissionBrokerModelController from "../../controller/model/PermissionBrokerModelController";
import BrokerToolsPanel from "./ibDashboard/BrokerToolsPanel";
import "../css/IbDashboard.css";
import "../css/MarketingTools.css";
import "../css/IbBrokerTools.css";
import "../../components/css/AppTopHeader.css";

const TAB_DAILY = "daily";
const TAB_BROKER = "broker";

const FILTER_ALL = "all";
const FILTER_POPULAR = "popular";
const FILTER_RECENT = "recent";

const inferMediaType = (fileName, mimeType) => {
  const lowerName = String(fileName || "").toLowerCase();
  const lowerMime = String(mimeType || "").toLowerCase();
  if (lowerMime.startsWith("video/")) return "video";
  if (lowerMime.startsWith("image/")) return "image";
  if (/\.(mp4|webm|ogg|mov|m4v)$/i.test(lowerName)) return "video";
  return "image";
};

const getConfigId = (row) => {
  const id =
    row?.MARKETING_CONFIG_ID ??
    row?.marketing_configuration?.ID ??
    row?.marketingConfiguration?.ID;
  return id != null && String(id).trim() !== "" ? String(id) : "";
};

const getConfigTitle = (row) =>
  String(
    row?.marketing_configuration?.TITLE ??
      row?.marketingConfiguration?.TITLE ??
      ""
  ).trim();

const getConfigDescription = (row) =>
  String(
    row?.marketing_configuration?.DESCRIPTION ??
      row?.marketingConfiguration?.DESCRIPTION ??
      row?.marketing_configuration?.SUBTITLE ??
      row?.marketingConfiguration?.SUBTITLE ??
      ""
  ).trim();

const parseRowDate = (row) => {
  const raw = row?.CREATED_AT ?? row?.created_at ?? row?.DATE ?? row?.date;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

const loadMarketingAttachment = async (fileName, blobUrls) => {
  const trimmed = String(fileName || "").trim();
  if (!trimmed) return null;

  try {
    const response = await getMarketingPlanAttachment(trimmed);
    const blob = response?.data;
    if (!blob || !(blob instanceof Blob)) return null;
    const ct = String(blob.type || "").toLowerCase();
    if (ct.includes("application/json") || ct.includes("text/html")) {
      return null;
    }
    const blobUrl = URL.createObjectURL(blob);
    blobUrls.push(blobUrl);
    return {
      url: blobUrl,
      mediaType: inferMediaType(trimmed, blob?.type),
      fileName: trimmed,
    };
  } catch (_) {
    return null;
  }
};

const rowToStoryItem = (row, loaded) => ({
  id: `marketing-plan-${row?.ID ?? loaded.fileName}`,
  mediaType: loaded.mediaType || "image",
  source: loaded.url,
  fileName: loaded.fileName,
  alt: getConfigTitle(row) || "Marketing template",
});

const triggerFileDownload = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "download";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function MarketingTools() {
  const { t } = useTranslation("mobile");
  const { error: showError, success: showSuccess } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [storyOpen, setStoryOpen] = useState(false);
  const [activeStories, setActiveStories] = useState([]);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  const [downloadingCategoryId, setDownloadingCategoryId] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState(TAB_DAILY);
  const blobUrlsRef = useRef([]);

  const currentStory = activeStories[activeStoryIndex] || null;

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { brokerPerm, brokerDevicePerm } = permissions;

  const canViewBrokerTools = useMemo(() => {
    if (!user) return false;
    if (!isBroker(user)) return false;
    return hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
    );
  }, [user, brokerPerm, brokerDevicePerm]);

  const mainTabs = useMemo(() => {
    const tabs = [
      {
        id: TAB_DAILY,
        label: t("marketing_tools_tab_daily_report", { defaultValue: "Daily Report" }),
      },
    ];
    if (canViewBrokerTools) {
      tabs.push({
        id: TAB_BROKER,
        label: t("marketing_tools_tab_broker_tools", { defaultValue: "Broker Tools" }),
      });
    }
    return tabs;
  }, [canViewBrokerTools, t]);

  useEffect(() => {
    if (activeMainTab === TAB_BROKER && !canViewBrokerTools) {
      setActiveMainTab(TAB_DAILY);
    }
  }, [activeMainTab, canViewBrokerTools]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("userData");
      if (stored) setUser(JSON.parse(stored));
    } catch (_) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    blobUrlsRef.current = [];

    (async () => {
      try {
        setLoading(true);
        const response = await getMarketingPlans();
        if (cancelled) return;

        const payload = response?.decryptedData || response?.data || {};
        if (Number(payload?.CODE) !== AppConstants.responseOk) {
          showError(
            "Error Occurred",
            payload?.MESSAGE || payload?.message || t("no_data_found", { defaultValue: "No data found" })
          );
          setCategories([]);
          return;
        }

        const rows = Array.isArray(payload?.DATA) ? payload.DATA : [];
        const grouped = new Map();

        for (const row of rows) {
          const configId = getConfigId(row);
          if (!configId) continue;
          if (!grouped.has(configId)) {
            grouped.set(configId, {
              id: configId,
              title: getConfigTitle(row) || t("marketing_tools_untitled", { defaultValue: "Untitled" }),
              description: getConfigDescription(row),
              items: [],
              latestAt: 0,
            });
          }
          const cat = grouped.get(configId);
          if (!cat.title && getConfigTitle(row)) cat.title = getConfigTitle(row);
          if (!cat.description && getConfigDescription(row)) {
            cat.description = getConfigDescription(row);
          }
          cat.items.push(row);
          cat.latestAt = Math.max(cat.latestAt, parseRowDate(row));
        }

        const categoriesWithMedia = [];
        for (const cat of grouped.values()) {
          const stories = [];
          let previewUrl = null;
          let previewMedia = "image";

          for (const row of cat.items) {
            const attachmentName = String(row?.ATTACHMENT || "").trim();
            if (!attachmentName) continue;
            const loaded = await loadMarketingAttachment(attachmentName, blobUrlsRef.current);
            if (!loaded?.url) continue;
            if (!previewUrl) {
              previewUrl = loaded.url;
              previewMedia = loaded.mediaType;
            }
            stories.push(rowToStoryItem(row, loaded));
          }

          if (stories.length === 0) continue;

          categoriesWithMedia.push({
            ...cat,
            count: stories.length,
            previewUrl,
            previewMedia,
            stories,
          });
        }

        if (!cancelled) setCategories(categoriesWithMedia);
      } catch (err) {
        if (!cancelled) {
          showError("Error Occurred", err?.message || "Failed to load marketing tools.");
          setCategories([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      blobUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
      blobUrlsRef.current = [];
    };
  }, [showError, t]);

  const filteredCategories = useMemo(() => {
    const list = [...categories];
    if (filter === FILTER_POPULAR) {
      list.sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
    } else if (filter === FILTER_RECENT) {
      list.sort((a, b) => b.latestAt - a.latestAt || a.title.localeCompare(b.title));
    } else {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }
    return list;
  }, [categories, filter]);

  const openCategoryStories = useCallback((category) => {
    if (!category?.stories?.length) return;
    setActiveStories(category.stories);
    setActiveStoryIndex(0);
    setStoryOpen(true);
  }, []);

  const downloadCategoryFiles = useCallback(
    async (category) => {
      const stories = category?.stories || [];
      if (!stories.length || downloadingCategoryId) return;

      setDownloadingCategoryId(category.id);
      try {
        let downloaded = 0;
        for (let i = 0; i < stories.length; i += 1) {
          const story = stories[i];
          if (!story?.source) continue;
          const fileName = story.fileName || `${category.title || "template"}-${i + 1}`;
          triggerFileDownload(story.source, fileName);
          downloaded += 1;
          if (i < stories.length - 1) {
            await wait(350);
          }
        }

        if (downloaded === 0) {
          showError(
            t("marketing_tools_download_failed", { defaultValue: "Download failed" }),
            t("no_data_found", { defaultValue: "No data found" })
          );
          return;
        }

        showSuccess(
          t("marketing_tools_download_started", { defaultValue: "Download started" }),
          t("marketing_tools_download_started_msg", {
            defaultValue: "{{count}} file(s) from {{title}}",
            count: downloaded,
            title: category.title,
          })
        );
      } catch (err) {
        showError(
          t("marketing_tools_download_failed", { defaultValue: "Download failed" }),
          err?.message || t("no_data_found", { defaultValue: "No data found" })
        );
      } finally {
        setDownloadingCategoryId(null);
      }
    },
    [downloadingCategoryId, showError, showSuccess, t]
  );

  const closeStories = useCallback(() => {
    setStoryOpen(false);
    setActiveStoryIndex(0);
  }, []);

  const goToPrevStory = useCallback(() => {
    setActiveStoryIndex((prev) => (prev <= 0 ? activeStories.length - 1 : prev - 1));
  }, [activeStories.length]);

  const goToNextStory = useCallback(() => {
    setActiveStoryIndex((prev) => {
      if (prev >= activeStories.length - 1) {
        setStoryOpen(false);
        return 0;
      }
      return prev + 1;
    });
  }, [activeStories.length]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeStories();
    };
    if (storyOpen) window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [storyOpen, closeStories]);

  useEffect(() => {
    if (!storyOpen || activeStories.length === 0) return undefined;
    const timerId = window.setInterval(() => {
      setActiveStoryIndex((prev) => {
        if (prev >= activeStories.length - 1) {
          setStoryOpen(false);
          return 0;
        }
        return prev + 1;
      });
    }, 15000);
    return () => window.clearInterval(timerId);
  }, [storyOpen, activeStories.length]);

  const pageTitle = t("marketing_tools", { defaultValue: "Marketing Plan" });
  const isDailyTab = activeMainTab === TAB_DAILY;

  return (
    <div className="ib-dashboard-page marketing-tools-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100 ib-dashboard-main-inner">
          <header className="marketing-tools-head">
            <div className="marketing-tools-head-row">
              <div>
                <h1 className="marketing-tools-title">{pageTitle}</h1>
                <p className="marketing-tools-sub">
                  {t("marketing_tools_sub", {
                    defaultValue: "Create stunning marketing materials with our templates.",
                  })}
                </p>
              </div>
              {isDailyTab && (
                <div className="marketing-tools-filters" role="tablist" aria-label={pageTitle}>
                  {[FILTER_ALL, FILTER_POPULAR, FILTER_RECENT].map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={filter === key}
                      className={`marketing-tools-filter-btn ${filter === key ? "marketing-tools-filter-btn--active" : ""}`}
                      onClick={() => setFilter(key)}
                    >
                      {t(`marketing_tools_filter_${key}`, {
                        defaultValue: key === FILTER_ALL ? "All" : key === FILTER_POPULAR ? "Popular" : "Recent",
                      })}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {mainTabs.length > 1 && (
              <nav className="marketing-tools-main-tabs" role="tablist" aria-label={pageTitle}>
                {mainTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeMainTab === tab.id}
                    className={`marketing-tools-main-tab ${
                      activeMainTab === tab.id ? "marketing-tools-main-tab--active" : ""
                    }`}
                    onClick={() => setActiveMainTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            )}
          </header>

          {!isDailyTab && canViewBrokerTools && <BrokerToolsPanel user={user} />}

          {isDailyTab && loading && (
            <div className="marketing-tools-grid" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className="marketing-tools-card marketing-tools-card--loading">
                  <div className="marketing-tools-card-media" />
                  <div className="marketing-tools-card-body">
                    <span className="ib-dashboard-skeleton ib-dashboard-skeleton--name" />
                    <span className="ib-dashboard-skeleton ib-dashboard-skeleton--sub" style={{ marginTop: 8 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isDailyTab && !loading && filteredCategories.length === 0 && (
            <div className="marketing-tools-empty">{t("no_data_found", { defaultValue: "No data found" })}</div>
          )}

          {isDailyTab && !loading && filteredCategories.length > 0 && (
            <div className="marketing-tools-grid">
              {filteredCategories.map((category) => (
                <article key={category.id} className="marketing-tools-card">
                  <div className="marketing-tools-card-media">
                    {category.previewUrl ? (
                      category.previewMedia === "video" ? (
                        <video src={category.previewUrl} muted playsInline />
                      ) : (
                        <img src={category.previewUrl} alt="" />
                      )
                    ) : (
                      <div className="marketing-tools-card-media-placeholder">
                        <i className="fa-regular fa-image" aria-hidden />
                      </div>
                    )}
                    <div className="marketing-tools-card-media-overlay">
                      <button
                        type="button"
                        className="marketing-tools-card-preview-eye"
                        onClick={() => openCategoryStories(category)}
                        aria-label={t("marketing_tools_preview", {
                          defaultValue: "Preview {{title}}",
                          title: category.title,
                        })}
                      >
                        <i className="fa-solid fa-eye" aria-hidden />
                      </button>
                    </div>
                  </div>
                  <div className="marketing-tools-card-body">
                    <h2 className="marketing-tools-card-title">{category.title}</h2>
                    {category.description ? (
                      <p className="marketing-tools-card-desc">{category.description}</p>
                    ) : null}
                    <div className="marketing-tools-card-footer">
                      <span className="marketing-tools-card-count">
                        {t("marketing_tools_template_count", {
                          defaultValue: "{{count}} templates",
                          count: category.count,
                        })}
                      </span>
                      <button
                        type="button"
                        className="marketing-tools-card-download"
                        onClick={() => downloadCategoryFiles(category)}
                        disabled={downloadingCategoryId === category.id}
                        aria-label={t("marketing_tools_download_all", {
                          defaultValue: "Download all templates in {{title}}",
                          title: category.title,
                        })}
                        title={t("marketing_tools_download_all", {
                          defaultValue: "Download all templates in {{title}}",
                          title: category.title,
                        })}
                      >
                        <i
                          className={`fa-solid ${
                            downloadingCategoryId === category.id ? "fa-spinner fa-spin" : "fa-download"
                          }`}
                          aria-hidden
                        />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {storyOpen && activeStories.length > 0 && (
        <div
          className="app-top-header-story-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("marketing_tools_story_viewer", { defaultValue: "Marketing templates" })}
        >
          <button
            type="button"
            className="app-top-header-story-backdrop"
            aria-label={t("cancel", { defaultValue: "Close" })}
            onClick={closeStories}
          />
          <div className="app-top-header-story-modal">
            <button
              type="button"
              className="app-top-header-story-close"
              aria-label={t("cancel", { defaultValue: "Close" })}
              onClick={closeStories}
            >
              <i className="fa-solid fa-xmark" aria-hidden />
            </button>
            <div
              className="app-top-header-story-progress"
              style={{ "--story-count": Math.max(activeStories.length, 1) }}
              aria-hidden
            >
              {activeStories.map((story, index) => (
                <span
                  key={story.id}
                  className={`app-top-header-story-progress-item ${
                    index < activeStoryIndex ? "app-top-header-story-progress-item--done" : ""
                  }`}
                >
                  {index === activeStoryIndex && (
                    <span
                      key={`${story.id}-${activeStoryIndex}`}
                      className="app-top-header-story-progress-item-fill app-top-header-story-progress-item-fill--active"
                    />
                  )}
                </span>
              ))}
            </div>
            <div className="app-top-header-story-body">
              <button
                type="button"
                className="app-top-header-story-nav app-top-header-story-nav--prev"
                aria-label={t("marketing_tools_prev_story", { defaultValue: "Previous" })}
                onClick={goToPrevStory}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden />
              </button>
              {currentStory?.mediaType === "video" ? (
                <video
                  src={currentStory?.source}
                  className="app-top-header-story-image"
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={currentStory?.source}
                  alt={currentStory?.alt || "Marketing template"}
                  className="app-top-header-story-image"
                />
              )}
              <button
                type="button"
                className="app-top-header-story-nav app-top-header-story-nav--next"
                aria-label={t("marketing_tools_next_story", { defaultValue: "Next" })}
                onClick={goToNextStory}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden />
              </button>
            </div>
            <div className="app-top-header-story-footer">
              <a
                href={currentStory?.source || "#"}
                download={currentStory?.fileName || `marketing-${activeStoryIndex + 1}`}
                className="app-top-header-story-download"
                aria-label={t("marketing_tools_download", { defaultValue: "Download" })}
                title={t("marketing_tools_download", { defaultValue: "Download" })}
              >
                <i className="fa-solid fa-download" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}